import { Button, Col, Flex, Layout, Row, Space, Tooltip, Typography, notification } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { defaultSearchData } from 'src/app/shared/components/common/table/table.constants'
import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import { useViewAuditHistory } from './useViewAuditHistory'
import { formatNumberWithCommas } from 'src/app/shared/utils'
import './index.scss'
import { Controller, useForm } from 'react-hook-form'
import { ArrowRightIcon, CalendarCustomIcon, ResetIconNormal, SearchIconNormal } from 'src/assets/icons'
import CustomRangePicker from 'src/app/shared/components/common/range-picker'
import { useDatePicker } from 'src/app/shared/hooks/useDatePicker'
import CustomSelectAll from 'src/app/shared/components/common/select-all'
import { useParams } from 'react-router-dom'
import { getAuditHistoryView, getUsersByAuditHistory } from 'src/app/shared/services/ViewAuditService'
import { getTransactionDetailById } from 'src/app/shared/services/ViewTransactionService'
import { initialTransaction } from '../view-record/index.constant'
import dayjs from 'dayjs'
import NewCommonTable from 'src/app/shared/components/common/new-table'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { useDisableFutureDates } from 'src/app/shared/hooks/useDisableFutureDates'
import { settingHeightTableWithFilter } from 'src/app/shared/constants/common.constants'

const ViewAuditHistory = (props: any) => {
  const { userPermissions } = useRolePermission()
  const { isGettingList, auditHistory, getAuditHistory } = useViewAuditHistory()
  const { disableFutureDates } = useDisableFutureDates()
  const { t } = useTranslation()
  const { id } = useParams()
  const [api, contextHolder] = notification.useNotification()
  const { getDatePickerDate, getCurrentDate } = useDatePicker()
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [users, setUsers] = useState<any>(null)
  const [transactionDetail, setTransactionDetail] = useState<any>(initialTransaction)
  const defaultSearchValues = {
    userIds: [null],
    date: [getCurrentDate().startOf('day'), getCurrentDate().startOf('day')],
  }

  const { handleSubmit, control, setValue, reset, getValues } = useForm<any>({
    mode: 'all',
    defaultValues: defaultSearchValues,
  })
  const getHeaderName = useCallback(
    (field: string): string => {
      return t(`AllTransactions:auditHistory.table_field.${field}`)
    },
    [t],
  )
  const { secCode, achOriginatorID, drcr, amount } = transactionDetail

  const generateCustomColumnDefs = useCallback((colDefs: Array<TableColumnDefs>): Array<TableColumnDefs> => {
    return colDefs.map((item, index) => {
      if (item.colId === 'dateChanged') {
        item.dataType = 'date'
        item.cellRenderer = (row: any) => {
          return row?.data[item.colId] ?? ''
        }
        item.cellStyle = {
          display: 'flex',
          justifyContent: 'center',
        }
      }

      return {
        ...item,
        flex: 1,
      }
    })
  }, [])

  const handleSearch = useCallback(
    async (value: Partial<any>) => {
      if (users) {
        const payload: any = {
          ...value,
          transactionId: id,
          from: getValues('date')?.[0] ? getDatePickerDate(getValues('date')?.[0]).toISOString() : '',
          to: getValues('date')?.[1] ? getDatePickerDate(getValues('date')?.[1]).toISOString() : '',
          dateRangeColumn: 'dateChanged',
          filterOption: 0,
        }

        payload.userIds = getValues('userIds')?.includes(null)
          ? users?.map((user: any) => user?.id) ?? []
          : getValues('userIds')
        await getAuditHistory(payload)
      }
    },
    [getAuditHistory, getDatePickerDate, getValues, users],
  )

  const handleDownload = useCallback(
    async (payload: any) => {
      const updatedPayload: any = {
        ...payload,
        transactionId: id,
        from: getValues('date')?.[0] ? getDatePickerDate(getValues('date')?.[0]).toISOString() : '',
        to: getValues('date')?.[1] ? getDatePickerDate(getValues('date')?.[1]).toISOString() : '',
        dateRangeColumn: 'dateChanged',
      }

      updatedPayload.userIds = getValues('userIds')?.includes(null)
        ? users?.map((user: any) => user?.id) ?? []
        : getValues('userIds')
      console.log('updatedPayload', updatedPayload, getValues('userIds')?.includes(null), users)
      return getAuditHistoryView(updatedPayload)
    },
    [getDatePickerDate, getValues, id, users],
  )

  const onSubmit = async (values: any) => {
    try {
      const payload: any = {
        ...defaultSearchData,
        transactionId: id,
        from: values?.date?.[0] ? getDatePickerDate(values?.date?.[0]).toISOString() : '',
        to: values?.date?.[1] ? getDatePickerDate(values?.date?.[1]).toISOString() : '',
        dateRangeColumn: 'dateChanged',
        filterOption: 0,
        isDesc: true,
      }

      payload.userIds = getValues('userIds')?.includes(null)
        ? users?.map((user: any) => user?.id) ?? []
        : getValues('userIds')
      await getAuditHistory(payload)
    } catch (error) {
      api.error({
        message: t('common:error'),
        description: `${error}`,
        placement: 'topRight',
      })
    }
  }

  const getAllUsersForTransaction = async (id: string) => {
    try {
      setLoadingUsers(true)
      const res = await getUsersByAuditHistory(id)
      if (res?.data) {
        setUsers(res?.data)
      }
    } catch (error: any) {
      api.error({
        message: t('common:error'),
        description: `${error}`,
        placement: 'topRight',
      })
    } finally {
      setLoadingUsers(false)
    }
  }

  const getDetailAuditTransaction = async (id: string) => {
    try {
      setLoadingUsers(true)
      const res = await getTransactionDetailById(id)
      if (res?.data) {
        setTransactionDetail(res?.data)
      }
    } catch (error: any) {
      api.error({
        message: t('common:error'),
        description: `${error}`,
        placement: 'topRight',
      })
    } finally {
      setLoadingUsers(false)
    }
  }

  useEffect(() => {
    if (id) {
      getAllUsersForTransaction(id)
      getDetailAuditTransaction(id)
    }
  }, [])

  useEffect(() => {
    if (users) {
      handleSearch({
        ...defaultSearchData,
        sortColumns: ['dateChanged'],
        isDesc: false,
      })
    }
  }, [users])
  return (
    <Layout.Content className='view-audit-container'>
      {contextHolder}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row gutter={24}>
          <Col className='view-audit-search'>
            <Controller
              control={control}
              name='userIds'
              render={({ field, fieldState }) => (
                <CustomSelectAll
                  label={t('AllTransactions:auditHistory.search_field.userName')}
                  placeholder={t('AllTransactions:auditHistory.search_field.userName')}
                  value={field.value}
                  onChange={(data: any) => {
                    if (!data || data?.length === 0) {
                      setValue('userIds', [null])
                    } else if (data?.includes(null)) {
                      if (data[0] === null) {
                        const newData = data?.slice(1)
                        setValue('userIds', [...newData])
                      } else {
                        setValue('userIds', [null])
                      }
                    } else {
                      setValue('userIds', data)
                    }
                  }}
                  maxTagCount={'responsive'}
                  maxTagPlaceholder={(omittedValues) => (
                    <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                      <span>+ {omittedValues.length}...</span>
                    </Tooltip>
                  )}
                  onBlur={field.onBlur}
                  error={fieldState.error}
                  options={
                    users?.map((item: any) => ({
                      label: item.userName,
                      value: item.id,
                    })) ?? []
                  }
                  haveAllOption
                  allowClear={!field.value?.includes(null)}
                  defaultActiveFirstOption
                  loading={loadingUsers}
                  defaultOption={{
                    label: 'All Username',
                    value: null,
                  }}
                  showSearch
                />
              )}
            />
          </Col>
          <Col className='view-audit-search'>
            <Controller
              name='date'
              control={control}
              render={({ field, fieldState }) => (
                <CustomRangePicker
                  label={t('AllTransactions:auditHistory.search_field.date')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error}
                  placeholder={[
                    t('AllTransactions:auditHistory.search_field.dateFrom'),
                    t('AllTransactions:auditHistory.search_field.dateTo'),
                  ]}
                  separator={<ArrowRightIcon className='view-audit-separator' />}
                  suffixIcon={<CalendarCustomIcon />}
                  allowEmpty={[true, true]}
                  disabledDate={disableFutureDates}
                />
              )}
            />
          </Col>
          <Col className='search-audit-btns'>
            <Button
              className='reset-button'
              onClick={async () => {
                reset(defaultSearchValues)
                await onSubmit(defaultSearchValues)
              }}
            >
              <ResetIconNormal />
            </Button>
            <Button
              type='primary'
              className='search-check-btn'
              htmlType='button'
              icon={<SearchIconNormal />}
              onClick={handleSubmit(onSubmit)}
            />
          </Col>
        </Row>
      </form>
      <Space className='info-wrapper'>
        <Flex className='info-content'>
          <Typography.Text className='info__title'>{t('AllTransactions:auditHistory.sec_code')}:</Typography.Text>
          <Typography.Text className='info__value'>{secCode}</Typography.Text>
        </Flex>
        <Flex className='info-content'>
          <Typography.Text className='info__title'>{t('AllTransactions:auditHistory.originatorID')}:</Typography.Text>
          <Typography.Text className='info__value'>{achOriginatorID}</Typography.Text>
        </Flex>
        <Flex className='info-content'>
          <Typography.Text className='info__title'>{t('AllTransactions:auditHistory.drcr')}:</Typography.Text>
          <Typography.Text className='info__value'>{drcr}</Typography.Text>
        </Flex>
        <Flex className='info-content'>
          <Typography.Text className='info__title'>{t('AllTransactions:auditHistory.amount')}:</Typography.Text>
          <Typography.Text className='info__value'>{amount >= 0 ? formatNumberWithCommas(amount) : ''}</Typography.Text>
        </Flex>
      </Space>
      <NewCommonTable
        translationNamespace='AllTransactions:auditHistory.table_field'
        rowData={auditHistory?.items}
        total={auditHistory?.total}
        displayAddBtn={false}
        displayUploadButton={false}
        displayActionColumn={false}
        displayDownloadBtn={userPermissions[ESubModules.ALL_TRANS]?.includes(EPermissions.DOWNLOAD)}
        loading={isGettingList}
        pageName={TABLE_NAME.VIEW_AUDIT_HISTORY}
        tableSettingName={TABLE_NAME.VIEW_AUDIT_HISTORY}
        handleSearch={handleSearch}
        handleDownload={handleDownload}
        getHeaderName={getHeaderName}
        generateCustomColumnDefs={generateCustomColumnDefs}
        settingHeightTable={settingHeightTableWithFilter}
      />
    </Layout.Content>
  )
}

export default ViewAuditHistory
